import { useEffect, useLayoutEffect, useRef } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';
import type { MapLayerMouseEvent } from 'react-map-gl';
import type { Position } from 'geojson';
import {
  deleteVertex,
  insertVertex,
  moveVertex,
} from 'components/geometry-editing';
import type { Pieces } from './types';
import { followDrag } from 'components/create-area-panel';
import { edgeAt } from './utils';

const SOURCE = 'area-edit-vertices';
/** The vertex dots, which Trace grabs as well. */
export const VERTEX_LAYER = 'area-edit-vertex-points';
const LAYER = VERTEX_LAYER;

interface VertexEditLayerProps {
  pieces: Pieces;
  color: string;
  /** Whether the dots show (the Vertices row in Layers). */
  visible: boolean;
  /** Whether the Move tool is on; off, the dots are scenery. */
  active: boolean;
  onChange: (pieces: Pieces) => void;
  /** Snapshots for undo, once per gesture. */
  onBeforeChange: () => void;
  /** Pulls a dragged or added vertex onto a nearby boundary, while Snap is on. */
  snapPosition?: (position: Position) => Position;
}

/**
 * Move vertices, as the mock has it: drag a vertex to move it, click an edge to add
 * one, double-click or right-click a vertex to remove it, down to a triangle. The
 * cursor reads `move` over a vertex and `copy` over the outline.
 *
 * @param {VertexEditLayerProps} props - The parts and their callbacks.
 * @return {JSX.Element} The vertex dots.
 */
export function VertexEditLayer({
  pieces,
  color,
  visible,
  active,
  onChange,
  onBeforeChange,
  snapPosition,
}: VertexEditLayerProps): JSX.Element {
  const { current: mapRef } = useMap();
  const latest = useRef({ pieces, onChange, onBeforeChange, snapPosition });
  useLayoutEffect(() => {
    latest.current = { pieces, onChange, onBeforeChange, snapPosition };
  }, [pieces, onChange, onBeforeChange, snapPosition]);
  /**
   * A position, snapped when Snap is on.
   *
   * @param {Position} position - Where the pointer is.
   * @return {Position} The position to use.
   */
  const placed = (position: Position): Position =>
    latest.current.snapPosition?.(position) ?? position;

  useEffect(() => {
    if (!mapRef || !active) return undefined;
    const map = mapRef.getMap();
    const canvas = map.getCanvas();
    let dragged = false;
    // Set while a vertex is held; stops the window listeners.
    let stopDrag: (() => void) | null = null;

    /**
     * The vertex under a point, if any.
     *
     * @param {MapLayerMouseEvent} event - The pointer event.
     * @return {{ p: number, i: number } | null} The vertex.
     */
    const vertexAt = (event: MapLayerMouseEvent) => {
      if (!map.getLayer(LAYER)) return null;
      const props = map.queryRenderedFeatures(event.point, {
        layers: [LAYER],
      })[0]?.properties as { p?: unknown; i?: unknown } | undefined;
      return typeof props?.p === 'number' && typeof props?.i === 'number'
        ? { p: props.p, i: props.i }
        : null;
    };
    /**
     * A position in screen px.
     *
     * @param {Position} position - [lng, lat].
     * @return {{ x: number, y: number }} The point.
     */
    const project = (position: Position) =>
      map.project(position as [number, number]);

    /**
     * Grabs a vertex.
     *
     * @param {MapLayerMouseEvent} event - The press.
     */
    const handleMouseDown = (event: MapLayerMouseEvent) => {
      // A new press starts a new gesture. Mapbox fires no click after a press
      // that moved, so the last drag's mark is cleared here, not by a click.
      dragged = false;
      if (event.originalEvent.button !== 0) return;
      const vertex = vertexAt(event);
      if (!vertex) return;
      event.preventDefault();
      map.dragPan.disable();
      /**
       * The vertex under the pointer.
       *
       * @param {Position} position - Where the pointer is.
       */
      const moveTo = (position: Position) => {
        const next = moveVertex(
          latest.current.pieces,
          vertex.p,
          vertex.i,
          placed(position)
        );
        if (next) latest.current.onChange(next);
      };
      stopDrag = followDrag(map, {
        move: (position) => {
          if (!dragged) latest.current.onBeforeChange();
          dragged = true;
          moveTo(position);
        },
        // Placed where the button came up too, so the vertex ends under it.
        end: (position) => {
          stopDrag = null;
          map.dragPan.enable();
          if (dragged) moveTo(position);
        },
      });
    };

    /**
     * Sets the cursor for what a click would do.
     *
     * @param {MapLayerMouseEvent} event - The move.
     */
    const handleMouseMove = (event: MapLayerMouseEvent) => {
      if (stopDrag) return;
      if (vertexAt(event)) canvas.style.cursor = 'move';
      else if (edgeAt(project, latest.current.pieces, event.point))
        canvas.style.cursor = 'copy';
      else canvas.style.cursor = '';
    };

    /**
     * A click on the outline adds a vertex there.
     *
     * @param {MapLayerMouseEvent} event - The click.
     */
    const handleClick = (event: MapLayerMouseEvent) => {
      if (dragged) {
        dragged = false;
        return;
      }
      if (vertexAt(event)) return;
      const edge = edgeAt(project, latest.current.pieces, event.point);
      if (!edge) return;
      const next = insertVertex(
        latest.current.pieces,
        edge.p,
        edge.i,
        placed([event.lngLat.lng, event.lngLat.lat])
      );
      if (!next) return;
      latest.current.onBeforeChange();
      latest.current.onChange(next);
    };

    /**
     * Removes the vertex under the pointer, down to a triangle.
     *
     * @param {MapLayerMouseEvent} event - The double-click or right-click.
     */
    const handleRemove = (event: MapLayerMouseEvent) => {
      const vertex = vertexAt(event);
      if (!vertex) return;
      event.preventDefault();
      const next = deleteVertex(latest.current.pieces, vertex.p, vertex.i);
      if (!next) return;
      latest.current.onBeforeChange();
      latest.current.onChange(next);
    };

    map.doubleClickZoom.disable();
    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('click', handleClick);
    map.on('dblclick', handleRemove);
    map.on('contextmenu', handleRemove);
    return () => {
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      stopDrag?.();
      map.off('click', handleClick);
      map.off('dblclick', handleRemove);
      map.off('contextmenu', handleRemove);
      map.dragPan.enable();
      map.doubleClickZoom.enable();
      canvas.style.cursor = '';
    };
  }, [mapRef, active]);

  return (
    <Source
      id={SOURCE}
      type="geojson"
      data={{
        type: 'FeatureCollection',
        features: visible
          ? pieces.flatMap((piece, p) =>
              piece.map((position, i) => ({
                type: 'Feature' as const,
                properties: { p, i },
                geometry: { type: 'Point' as const, coordinates: position },
              }))
            )
          : [],
      }}
    >
      <Layer
        id={LAYER}
        type="circle"
        paint={{
          'circle-radius': 5,
          'circle-color': '#ffffff',
          'circle-stroke-color': color,
          'circle-stroke-width': 2,
        }}
      />
    </Source>
  );
}
